import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  cancelRun,
  runDrawerQuery,
  runJob,
  runsShellQuery,
  runsTableQuery,
  type RunRow,
} from '../../api/admRuns'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField, WindowPicker } from './AdmFilterBar'
import { AdmViews } from './AdmViews'
import { ScanLiveStrip } from './ScanLiveStrip'
import { useScanLive } from '../../api/admScanLive'
import { RunDrawerBody, esc, progressCellHtml, statePillHtml } from './AdmRunBits'

/**
 * ADM -> Discovery runs, din `views/version-3/softsee/adm/runs/index.php` si
 * din tabelul lui din `public/js/adm.js`.
 *
 * UN RAND PE INTERVAL PE SCANARE, cel mai nou intai. Istoricul creste cu o
 * scanare pe interval pe fiecare rulare programata, deci pe o instalare
 * ocupata tabelul asta depaseste browserul in cateva saptamani - de-aia
 * paginarea, cautarea si sortarea sunt ale SERVERULUI, ca in portal.
 *
 * FARA BUTON "OPEN". Randul se desfasoara, si pagina intreaga e legata din
 * sertar. "Cancel" ramane: el FACE ceva, nu duce undeva - si se deseneaza doar
 * cand serverul spune ca randul chiar se poate opri (`canCancel`), fiindca
 * refuzul e tot pe server.
 *
 * Foaia e cea a portalului (`public/css/adm.css`), deci pastilele, bara de
 * progres si lista arata la fel ca inainte de mutare.
 */

const STYLES = ['css/adm.css']

/**
 * Coloanele listei, cu acelasi HTML pe care il scria `adm.js`.
 *
 * Coloana clientului lipseste: ecranele astea arata UN client (cel din antet),
 * deci ar fi repetat acelasi nume pe fiecare rand - `adm_showing_all_companies()`
 * din portal raspunde de mult `false`. Indicii de aici sunt cei pe care ii
 * asteapta `DbalRunsSource::RUN_ORDER`, si cele doua liste trebuie citite
 * impreuna.
 */
function runColumns(canWrite: boolean): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      className: 'text-center',
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    { index: 1, title: 'Started', orderable: true, visible: true, className: 'adm-nowrap', html: (row) => esc(row.startedAt) },
    { index: 2, title: 'Type', orderable: true, visible: true, html: (row) => esc(row.typeLabel) },
    { index: 3, title: 'Relay', orderable: true, visible: true, html: (row) => esc(row.relayName) },
    {
      index: 4,
      title: 'Progress',
      orderable: true,
      visible: true,
      width: 170,
      html: (row) => progressCellHtml(row as unknown as RunRow),
    },
    { index: 5, title: 'New devices', orderable: true, visible: true, className: 'adm-numeric', html: (row) => esc(row.ciCreated) },
    { index: 6, title: 'Failures', orderable: true, visible: true, className: 'adm-numeric', html: (row) => esc(row.errorsCount) },
    {
      index: 7,
      title: 'State',
      orderable: true,
      visible: true,
      html: (row) => {
        const r = row as unknown as RunRow
        return statePillHtml(r.state, r.stateLabel)
      },
    },
    {
      index: 8,
      title: '',
      orderable: false,
      visible: true,
      width: 110,
      className: 'text-right adm-nowrap',
      html: (row) => {
        const r = row as unknown as RunRow
        if (!canWrite || !r.canCancel) return ''
        return `<button type="button" class="btn btn-danger btn-xs admCancelRun" data-id="${r.ID}" title="Cancel this run"><i class="fas fa-times"></i></button>`
      },
    },
  ]
}

/** Sertarul unei scanari: se cere abia cand randul chiar se deschide. */
function RunDrawer({ id }: { id: number }) {
  const drawer = useQuery(runDrawerQuery(id))

  if (drawer.isPending) {
    return (
      <div className="adm-expand adm-muted">
        <i className="fas fa-circle-notch fa-spin"></i> Loading…
      </div>
    )
  }

  if (drawer.isError || !drawer.data) {
    return <div className="adm-expand adm-muted">This run could not be loaded.</div>
  }

  return <RunDrawerBody run={drawer.data.run} breakdown={drawer.data.breakdown} rows={drawer.data.rows} total={drawer.data.total} />
}

export function RunsPage() {
  const shellData = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shellData.data?.version ?? '')

  const [days, setDays] = useState(30)
  const [relay, setRelay] = useState('')
  const [type, setType] = useState('')
  const [runState, setRunState] = useState('')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [pendingCancel, setPendingCancel] = useState<RunRow | null>(null)
  const [job, setJob] = useState('')
  const [jobOutput, setJobOutput] = useState('')
  const [jobRunning, setJobRunning] = useState(false)

  /* Se cauta dupa ce omul se opreste din scris: lista vine de pe server, deci
     fiecare apasare ar fi insemnat o interogare. */
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const shell = useQuery(runsShellQuery(days, relay, type, runState))
  const [state, setState] = useTableState('admRuns', {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })

  const table = useQuery({
    ...runsTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderDir: state.order.dir,
      search: debounced,
      days,
      relay_device_id: relay,
      run_type: type,
      run_state: runState,
    }),
    placeholderData: keepPreviousData,
    /* Lista se reimprospateaza singura la fiecare cincisprezece secunde, ca in
       portal: o scanare care merge isi schimba progresul sub ochii cuiva. */
    refetchInterval: 15000,
  })

  /* Scanarile in curs, live pe socket (scanTicket / scanState) - si tabelul
     se reciteste la fiecare cadru, nu doar la 15 s. */
  const live = useScanLive(0, true)
  useEffect(() => {
    if (live.frame > 0) void table.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.frame])

  const canWrite = shell.data?.canWrite ?? false
  const columns = useMemo(() => runColumns(canWrite), [canWrite])

  /* Butonul de anulare e scris in HTML-ul celulei, ca in portal: clicul se
     prinde o data, pe tabel. */
  useEffect(() => {
    const host = document.getElementById('admRunsTable')
    if (!host) return
    const onClick = (event: Event) => {
      const target = (event.target as HTMLElement).closest('.admCancelRun')
      if (!target) return
      const id = Number(target.getAttribute('data-id'))
      const row = (table.data?.data ?? []).find((r) => r.ID === id)
      if (row) setPendingCancel(row)
    }
    host.addEventListener('click', onClick)
    return () => host.removeEventListener('click', onClick)
  }, [table.data])

  const stop = async () => {
    if (!pendingCancel) return
    try {
      const answer = await cancelRun(pendingCancel.ID)
      toast(answer.message, 'Discovery runs', answer.status === 'OK' ? 'success' : 'error')
      setPendingCancel(null)
      void table.refetch()
      void shell.refetch()
    } catch (error) {
      toast((error as Error).message, 'Discovery runs', 'error')
    }
  }

  const start = async () => {
    if (job === '') return
    setJobRunning(true)
    setJobOutput('')
    try {
      const answer = await runJob(job)
      setJobOutput(answer.output ?? '')
      toast(answer.message, 'Discovery runs', answer.status === 'OK' ? 'success' : 'error')
    } catch (error) {
      toast((error as Error).message, 'Discovery runs', 'error')
    } finally {
      setJobRunning(false)
    }
  }

  /* Cifra vine cu LISTA cand lista a raspuns, si din cadru pana atunci: doua
     cereri s-ar fi intors la momente diferite, iar banda ar fi spus "3 running"
     deasupra unui tabel in care se terminasera doua. */
  const active = table.data?.active ?? shell.data?.active ?? 0

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="blue-hoki"
          icon="fa-play-circle"
          title="Discovery runs"
          note="one row per address range per scan, newest first"
          stats={[{ value: active.toLocaleString('en-US'), label: 'running now', tone: active > 0 ? 'blue-hoki' : 'grey' }]}
          actions={
            canWrite && shell.data?.cronConfigured ? (
              <button type="button" className="btn btn-default btn-sm" id="admRunJobOpen" onClick={() => setJob(shell.data?.jobs[0]?.value ?? '')}>
                <i className="fas fa-bolt"></i> Run a job now
              </button>
            ) : undefined
          }
        />

        <AdmViews views={shell.data?.views ?? []} current="runs" />

        <AdmFilterBar>
          <AdmField label="Search" htmlFor="runSearch" wide>
            <input
              type="text"
              className="form-control input-sm"
              id="runSearch"
              placeholder="customer, or relay"
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </AdmField>

          <AdmField label="Relay" htmlFor="admRelay">
            <select className="form-control input-sm" id="admRelay" value={relay} onChange={(e) => setRelay(e.target.value)}>
              <option value="">Any relay</option>
              {(shell.data?.relays ?? []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </AdmField>

          <AdmField label="Type" htmlFor="admRunType">
            <select className="form-control input-sm" id="admRunType" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Any type</option>
              {(shell.data?.runTypes ?? []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </AdmField>

          <AdmField label="State" htmlFor="admRunState">
            <select className="form-control input-sm" id="admRunState" value={runState} onChange={(e) => setRunState(e.target.value)}>
              <option value="">Any state</option>
              {(shell.data?.states ?? []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </AdmField>

          <WindowPicker windows={shell.data?.windows ?? [1, 7, 30, 90]} value={days} onChange={setDays} />
        </AdmFilterBar>

        <ScanLiveStrip live={live} onReload={() => void table.refetch()} />

        <div id="admRunsTable">
          <DataTable
            id="admRuns"
            columns={columns}
            rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
            total={table.data?.recordsTotal ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            onRetry={() => void table.refetch()}
            state={state}
            onState={setState}
            rowId={(r) => String(r.DT_RowId ?? r.ID)}
            details={(row) => <RunDrawer id={Number(row.ID)} />}
            footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
            emptyHint="No scan has run in this period."
          />
        </div>
      </div>

      <Modal
        id="admCancelRunModal"
        open={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        title="Cancel this run"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setPendingCancel(null)}>
              Leave it running
            </button>
            <button type="button" className="btn btn-danger" id="admCancelRunConfirm" onClick={() => void stop()}>
              Cancel the run
            </button>
          </>
        }
      >
        <p>
          Run #{pendingCancel?.ID} on {pendingCancel?.relayName || 'this relay'} stops where it is.
        </p>
        <p className="adm-muted">
          {/* Spus pe fata: ce a apucat sa gaseasca ramane, si asta e deosebirea
              intre a opri o scanare si a o sterge. */}
          What it has already found stays — the addresses it has not reached yet simply will not be scanned. The next scheduled
          scan covers them again.
        </p>
      </Modal>

      <Modal
        id="admRunJobModal"
        open={job !== ''}
        onClose={() => {
          setJob('')
          setJobOutput('')
        }}
        title="Run a job now"
        footer={
          <>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => {
                setJob('')
                setJobOutput('')
              }}
            >
              Close
            </button>
            <button type="button" className="btn btn-danger" id="admRunJobStart" disabled={jobRunning} onClick={() => void start()}>
              {jobRunning ? 'Running…' : 'Run it'}
            </button>
          </>
        }
      >
        <p className="adm-muted">
          {/* De ce exista butonul asta: treburile sunt programate, si pentru
              inventar aia e bine - corelarea se uita la observatii adunate, nu
              la intamplari singuratice. Trei momente le cer acum: o
              demonstratie, un diagnostic, si primul import al unui client nou. */}
          These jobs are scheduled. Starting one by hand is for a demonstration, a diagnosis, or the first import for a new
          customer — the three moments when the next quarter hour is too late.
        </p>
        <div className="form-group">
          <label htmlFor="admJob">Job</label>
          <select className="form-control" id="admJob" value={job} onChange={(e) => setJob(e.target.value)}>
            {(shell.data?.jobs ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {jobOutput !== '' ? (
          <pre className="adm-muted" style={{ fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
            {jobOutput}
          </pre>
        ) : null}
      </Modal>
    </Content>
  )
}
